<template>
  <div :class="outerContainerClasses">
    <!-- Main form field container -->
    <div :class="containerClasses">
      <label :for="name" :class="labelClasses" :style="labelStyles">
        {{ label }}
        <span v-if="required && showRequiredIndicator" class="text-red-400 ml-1">*</span>
      </label>

      <div :class="inputWrapperClasses">
        <input
          :id="name"
          :name="name"
          :type="effectiveType"
          :inputmode="inputMode"
          :placeholder="placeholder"
          :class="[inputClasses, { 'red-border': hasError }, { 'input-required': isRequiredEmpty }]"
          :value="inputValue"
          @input="handleInput"
          @blur="handleBlur"
          @keydown="handleKeydown"
          @keypress="handleKeypress"
          v-bind:style="computedInputStyles"
          :disabled="disabled"
          :readonly="readonly"
          :min="min"
          :max="max"
          :step="step"
          :autocomplete="autocomplete"
          :pattern="pattern"
          :maxlength="maxlength"
          :minlength="minlength"
        />

        <!-- Absolute positioned error message -->
        <div
          v-if="hasError && showErrors && errorPosition === 'absolute'"
          :class="errorMessageAbsoluteClasses"
          :style="errorMessageStyles"
        >
          {{ errorMessage }}
        </div>

        <!-- Below input positioned error message -->
        <div
          v-if="hasError && showErrors && errorPosition === 'below'"
          :class="errorMessageBelowClasses"
          :style="errorMessageStyles"
        >
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useField } from 'vee-validate';
import { computed, toRef, watch } from 'vue';
import { defaultFormStyles, mergeStyles } from '@/validations/styles/themeConfig.js';

export default {
  name: 'ValidatedInput',
  props: {
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'text'
    },
    placeholder: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    showErrors: {
      type: Boolean,
      default: false // Disabled by default to match existing behavior
    },
    showRequiredIndicator: {
      type: Boolean,
      default: false // Disabled by default to match existing behavior
    },
    errorPosition: {
      type: String,
      default: 'below', // 'below', 'absolute', 'none'
      validator: (value) => ['below', 'absolute', 'none'].includes(value)
    },
    // For backward compatibility with existing components
    modelValue: {
      type: [String, Number],
      default: ''
    },
    // Validation rules (optional, can be used without form context)
    rules: {
      type: [String, Object, Function],
      default: undefined
    },
    // Style customization
    customStyles: {
      type: Object,
      default: () => ({})
    },
    // Alternative to use existing FormInput styling exactly
    useExistingStyle: {
      type: Boolean,
      default: true
    },
    // HTML input attributes
    min: {
      type: [String, Number],
      default: undefined
    },
    max: {
      type: [String, Number],
      default: undefined
    },
    step: {
      type: [String, Number],
      default: undefined
    },
    autocomplete: {
      type: String,
      default: undefined
    },
    pattern: {
      type: String,
      default: undefined
    },
    maxlength: {
      type: [String, Number],
      default: undefined
    },
    minlength: {
      type: [String, Number],
      default: undefined
    },
    // Prevent invalid input based on type
    preventInvalidInput: {
      type: Boolean,
      default: true
    },
    // Prevent negative numbers from being typed
    preventNegative: {
      type: Boolean,
      default: false
    },
    showPrevalidationWarning: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const name = toRef(props, 'name');

    // Use VeeValidate's useField composable
    const {
      value: inputValue,
      errorMessage,
      handleBlur,
      handleChange,
      meta
    } = useField(name, props.rules, {
      initialValue: props.modelValue,
      validateOnValueUpdate: true,
      // Sync manual (handleInput + watch abaixo): o sync automático do
      // vee-validate sobrescreve estados intermédios de digitação ("12.")
      // quando o pai normaliza o valor (parseFloat), repondo o campo a 0.
      syncVModel: false
    });

    // Campos numéricos decimais são renderizados como type="text" com
    // inputmode="decimal": o type="number" nativo devolve "" para valores
    // intermédios ("12.") e não permite inserir vírgula, o que impedia
    // digitar separadores decimais. Inteiros mantêm type="number" (spinners).
    const isIntegerOnly = computed(() => {
      return props.step === 1 || props.step === '1' ||
        (props.step === undefined && props.min !== undefined && (props.min >= 0 || props.min === '0'));
    });

    const isDecimalNumber = computed(() => {
      return props.type === 'number' && !isIntegerOnly.value;
    });

    const effectiveType = computed(() => {
      return isDecimalNumber.value ? 'text' : props.type;
    });

    const inputMode = computed(() => {
      return isDecimalNumber.value ? 'decimal' : undefined;
    });

    // Computed property for error state
    const hasError = computed(() => {
      return !!(errorMessage.value && (meta.touched || meta.dirty));
    });

    const isRequiredEmpty = computed(() => {
      if (!props.required || !props.showPrevalidationWarning) return false;
      const val = inputValue.value;
      return val === null || val === undefined || val === '';
    });

    // Style configuration
    const styleConfig = computed(() => {
      if (props.useExistingStyle) {
        // Use exact same styles as existing forms - flex layout with specific widths
        return {
          container: {
            classes: 'flex justify-between items-center'
          },
          label: {
            classes: 'text-blue-100 text-right w-1/6',
            styles: 'color: var(--label);'
          },
          input: {
            classes: 'rounded p-2 text-xs placeholder-gray-500 w-full',
            styles: {
              normal: {},
              error: {
                backgroundColor: 'var(--obrigatorio)',
                color: 'var(--text)'
              }
            }
          },
          errorMessage: {
            classes: 'text-xs mt-1',
            styles: 'color: var(--vermelho-2, #FD5D5D);'
          },
          errorMessageAbsolute: {
            classes: 'absolute top-full left-0 text-xs mt-1 z-10 bg-white px-2 py-1 rounded shadow-md border border-red-300',
            styles: 'color: var(--vermelho-2 , #FD5D5D);'
          },
          errorMessageBelow: {
            classes: 'text-xs mt-1 w-full',
            styles: 'color: var(--vermelho-2, #FD5D5D);'
          }
        };
      }
      return mergeStyles(props.customStyles);
    });

    // Computed styles and classes
    const outerContainerClasses = computed(() => {
      return 'relative'; // For absolute positioning of errors
    });

    const containerClasses = computed(() => {
      return styleConfig.value.container.classes;
    });

    const inputWrapperClasses = computed(() => {
      // Check if using custom styles and custom inputWrapper is provided
      if (!props.useExistingStyle && props.customStyles?.inputWrapper?.classes) {
        const baseClasses = props.errorPosition === 'absolute' ? 'relative' : '';
        return `${baseClasses} ${props.customStyles.inputWrapper.classes}`.trim();
      }
      // Default behavior for existing style
      return props.errorPosition === 'absolute' ? 'relative w-3/4' : 'w-3/4';
    });

    const labelClasses = computed(() => {
      return styleConfig.value.label.classes;
    });

    const labelStyles = computed(() => {
      return styleConfig.value.label.styles;
    });

    const inputClasses = computed(() => {
      return styleConfig.value.input.classes;
    });

    const computedInputStyles = computed(() => {
      let state = 'normal';
      if (props.disabled) {
        state = 'disabled';
      } else if (hasError.value) {
        state = 'error';
      }
      return styleConfig.value.input.styles[state] || styleConfig.value.input.styles.normal;
    });

    const errorMessageClasses = computed(() => {
      return styleConfig.value.errorMessage.classes;
    });

    const errorMessageAbsoluteClasses = computed(() => {
      return styleConfig.value.errorMessageAbsolute?.classes || 'absolute top-full left-0 text-xs mt-1 z-10 bg-white px-2 py-1 rounded shadow-md border border-red-300';
    });

    const errorMessageBelowClasses = computed(() => {
      return styleConfig.value.errorMessageBelow?.classes || 'text-xs mt-1 w-full';
    });

    const errorMessageStyles = computed(() => {
      return styleConfig.value.errorMessage.styles;
    });

    // Handle input changes
    const handleInput = (event) => {
      let value = event.target.value;

      if (isDecimalNumber.value && typeof value === 'string') {
        // Accept comma as decimal separator (PT convention) — convert to dot.
        // Remove também caracteres inválidos colados (o keypress não cobre paste).
        const sanitized = value.replace(/,/g, '.').replace(/[^0-9.-]/g, '');
        if (sanitized !== value) {
          const caret = event.target.selectionStart;
          value = sanitized;
          event.target.value = value;
          if (caret !== null) {
            try { event.target.setSelectionRange(caret, caret); } catch (e) { /* noop */ }
          }
        }
      }

      // Prevent negative values if preventNegative is true
      if (props.preventNegative && props.type === 'number' && parseFloat(value) < 0) {
        value = '0';
        event.target.value = value;
      }

      // Update VeeValidate field value
      handleChange(value);

      // Emit for v-model compatibility
      emit('update:modelValue', value);
    };

    // Prevent invalid keypress based on input type
    const handleKeypress = (event) => {
      if (!props.preventInvalidInput) return;

      if (props.type === 'number') {
        const char = String.fromCharCode(event.which || event.keyCode);
        const currentValue = event.target.value;

        // Allow control characters (backspace, delete, tab, escape, enter)
        if (event.keyCode < 32) {
          return;
        }

        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
        if (event.ctrlKey && [65, 67, 86, 88, 90].includes(event.keyCode)) {
          return;
        }

        if (isIntegerOnly.value) {
          // For integer-only inputs, only allow digits
          if (!/[0-9]/.test(char)) {
            event.preventDefault();
            return;
          }
        } else {
          // For decimal inputs
          // Allow digits
          if (/[0-9]/.test(char)) {
            return;
          }

          // Allow decimal point only once and not at the beginning
          if (char === '.' && currentValue.indexOf('.') === -1 && currentValue.length > 0) {
            return;
          }

          // Accept comma as decimal separator (PT convention) — deixa passar;
          // handleInput converte para ponto (o campo é type="text" interno)
          if (char === ',' && currentValue.indexOf('.') === -1 && currentValue.indexOf(',') === -1 && currentValue.length > 0) {
            return;
          }

          // Allow minus sign only at the beginning and if min is not set or allows negative and preventNegative is false
          if (char === '-' && currentValue.length === 0 && !props.preventNegative && (props.min === undefined || props.min < 0)) {
            return;
          }

          // Block everything else
          event.preventDefault();
        }
      }
    };

    // Handle keydown for additional control
    const handleKeydown = (event) => {
      if (!props.preventInvalidInput) return;

      if (props.type === 'number') {
        // Allow: backspace(8), delete(46), tab(9), escape(27), enter(13)
        // Allow: home(36), end(35), left(37), right(39), up(38), down(40)
        const allowedKeys = [8, 9, 13, 27, 35, 36, 37, 38, 39, 40, 46];

        if (allowedKeys.includes(event.keyCode)) {
          return;
        }

        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
        if (event.ctrlKey && [65, 67, 86, 88, 90].includes(event.keyCode)) {
          return;
        }

        // Allow: number keys (48-57 on main keyboard, 96-105 on numpad)
        if ((event.keyCode >= 48 && event.keyCode <= 57) ||
            (event.keyCode >= 96 && event.keyCode <= 105)) {
          return;
        }

        // Allow decimal point (190 on main keyboard, 110 on numpad) only for non-integer inputs
        if (!isIntegerOnly.value && (event.keyCode === 190 || event.keyCode === 110)) {
          const currentValue = event.target.value;
          if (currentValue.indexOf('.') === -1 && currentValue.length > 0) {
            return;
          }
        }

        // Allow comma (188) for non-integer inputs — handleKeypress decide e handleInput converte para ponto
        if (!isIntegerOnly.value && event.keyCode === 188) {
          return;
        }

        // Allow minus sign only for inputs that can be negative and preventNegative is false
        if (event.keyCode === 189 && !props.preventNegative && (props.min === undefined || props.min < 0)) {
          const currentValue = event.target.value;
          if (currentValue.length === 0) {
            return;
          }
        }

        // Block everything else
        event.preventDefault();
      }
    };

    // Watch for external modelValue changes (for backward compatibility)
    watch(() => props.modelValue, (newValue) => {
      if (newValue === inputValue.value) return;
      // Não sobrescrever estados intermédios de digitação ("12.", "12,50"):
      // o pai normaliza com parseFloat e devolveria 12, apagando o separador
      if (props.type === 'number') {
        const parsedNew = parseFloat(newValue);
        const parsedCurrent = parseFloat(inputValue.value);
        if (!isNaN(parsedNew) && parsedNew === parsedCurrent) return;
      }
      inputValue.value = newValue;
    });

    return {
      inputValue,
      effectiveType,
      inputMode,
      errorMessage,
      handleInput,
      handleBlur,
      handleChange,
      handleKeypress,
      handleKeydown,
      meta,
      hasError,
      isRequiredEmpty,
      styleConfig,
      outerContainerClasses,
      containerClasses,
      inputWrapperClasses,
      labelClasses,
      labelStyles,
      inputClasses,
      computedInputStyles,
      errorMessageClasses,
      errorMessageAbsoluteClasses,
      errorMessageBelowClasses,
      errorMessageStyles
    };
  }
};
</script>

<style scoped>
.red-border {
  background-color: var(--obrigatorio);
}

.red-border:focus {
  border: 1px solid red;
}

/* Remove custom borders and focus styles to match existing behavior */
input:focus {
  outline: none;
}

</style>